import React, { useEffect, useState, useRef } from 'react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon } from 'lucide-react';
import { Lesson } from '../../src/services/lessonsService';
import { toLocal } from '../../src/utils/datetime';
import '../../styles/calendar-week.css';
import { LessonChip } from './LessonChip';
import { CalendarViewSelector } from './CalendarViewSelector';
// ADDED: Index-based color palette for lesson chips
const LESSON_CHIP_COLORS = ['#A78BFA', '#6EE7B7', '#7DD3FC', '#FCA5A5', '#5EEAD4', '#FCD34D', '#FDA4AF', '#A5B4FC' // Indigo
];
// ADDED: Helper function to get lesson chip color by index
const getLessonChipColor = (index: number): string => {
  return LESSON_CHIP_COLORS[index % LESSON_CHIP_COLORS.length];
};
type CalendarView = 'month' | 'week' | 'day';
interface TeacherCalendarWeekProps {
  weekDays: Date[];
  lessons: Lesson[];
  isDark: boolean;
  onTimeSlotClick: (date: Date, hour: number, minute: number) => void;
  onLessonClick: (lesson: Lesson, e: React.MouseEvent) => void;
  startHour?: number;
  endHour?: number;
  onViewChange: (view: CalendarView) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onTodayClick: () => void;
  popoverEvent?: Lesson | null; // ADDED: Receive popover state from parent
  onClosePopover?: () => void; // ADDED: Receive close function from parent
}
export const TeacherCalendarWeek: React.FC<TeacherCalendarWeekProps> = ({
  weekDays,
  lessons,
  isDark,
  onTimeSlotClick,
  onLessonClick,
  startHour = 7,
  endHour = 21,
  onViewChange,
  onPrevWeek,
  onNextWeek,
  onTodayClick,
  popoverEvent,
  onClosePopover
}) => {
  const [currentTimePosition, setCurrentTimePosition] = useState<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const timeGridRef = useRef<HTMLDivElement>(null);
  const hourHeight = 60; // Height in pixels for each hour in the grid
  // Format date for display
  const formatDateHeader = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'numeric',
      day: 'numeric'
    });
  };
  // Check if date is today
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };
  // Update current time indicator position
  useEffect(() => {
    const updateTimeIndicator = () => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      if (hours >= startHour && hours <= endHour) {
        const hourPosition = (hours - startHour) * hourHeight;
        const minutePosition = minutes / 60 * hourHeight;
        setCurrentTimePosition(hourPosition + minutePosition);
      } else {
        setCurrentTimePosition(null);
      }
    };
    // Update immediately and then every minute
    updateTimeIndicator();
    const interval = setInterval(updateTimeIndicator, 60000);
    return () => clearInterval(interval);
  }, [startHour, endHour]);
  // Scroll to current time on initial render
  useEffect(() => {
    if (scrollContainerRef.current && currentTimePosition !== null) {
      const scrollPosition = currentTimePosition - window.innerHeight / 3;
      scrollContainerRef.current.scrollTop = Math.max(0, scrollPosition);
    }
  }, []);
  // Generate time slots
  const timeSlots = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    timeSlots.push(hour);
  }
  // Get formatted time string (12-hour format)
  const getFormattedTime = (hour: number): string => {
    const suffix = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 === 0 ? 12 : hour % 12;
    return `${displayHour}:00 ${suffix}`;
  };
  // Position lessons in the grid - FIXED positioning and overflow
  const renderLessons = () => {
    const lessonElements: JSX.Element[] = [];
    // ADDED: Group lessons by day to assign index-based colors
    const lessonsByDay = new Map<number, Lesson[]>();
    lessons.forEach(lesson => {
      try {
        const startTime = toLocal(lesson.startAt, 'America/Los_Angeles');
        const dayIndex = startTime.getDay();
        if (!lessonsByDay.has(dayIndex)) {
          lessonsByDay.set(dayIndex, []);
        }
        lessonsByDay.get(dayIndex)!.push(lesson);
      } catch (err) {
        console.error('Error grouping lesson:', err);
      }
    });
    // Sort lessons within each day by start time
    lessonsByDay.forEach(dayLessons => {
      dayLessons.sort((a, b) => {
        const aTime = toLocal(a.startAt, 'America/Los_Angeles');
        const bTime = toLocal(b.startAt, 'America/Los_Angeles');
        return aTime.getTime() - bTime.getTime();
      });
    });
    lessons.forEach(lesson => {
      try {
        // Convert UTC times to local for display
        const startTime = toLocal(lesson.startAt, 'America/Los_Angeles');
        const endTime = toLocal(lesson.endAt, 'America/Los_Angeles');
        // Determine which day column this belongs to
        const lessonDay = startTime.getDay();
        const dayIndex = lessonDay;
        // Check if the lesson falls within our week view
        const lessonDate = new Date(startTime).setHours(0, 0, 0, 0);
        const weekDayDate = new Date(weekDays[dayIndex]).setHours(0, 0, 0, 0);
        if (lessonDate !== weekDayDate) {
          return;
        }
        // ADDED: Get the index of this lesson within its day for color assignment
        const dayLessons = lessonsByDay.get(dayIndex) || [];
        const lessonIndexInDay = dayLessons.findIndex(l => l.id === lesson.id);
        // Calculate position and height
        const startHourDecimal = startTime.getHours() + startTime.getMinutes() / 60;
        const endHourDecimal = endTime.getHours() + endTime.getMinutes() / 60;
        // Skip if outside our time range
        if (endHourDecimal < startHour || startHourDecimal > endHour) {
          return;
        }
        // FIXED: Account for border accumulation (1px border-t per hour cell)
        // Each hour cell has a 1px top border, so we need to add the cumulative border height
        const bordersAbove = Math.floor(startHourDecimal - startHour);
        // FIXED: Add vertical padding to center the lesson card within its time slot
        const verticalPadding = 4; // 4px padding on top and bottom for visual centering
        const top = Math.max(0, (startHourDecimal - startHour) * hourHeight + bordersAbove + verticalPadding);
        const height = Math.min((endHour - startHourDecimal + 1) * hourHeight, (endHourDecimal - startHourDecimal) * hourHeight) - verticalPadding * 2; // Subtract padding from both top and bottom
        // FIXED: Use calc with padding built into the width calculation
        // This ensures chips stay within column bounds
        const left = `calc((100% / 7) * ${dayIndex} + 4px)`;
        const width = 'calc((100% / 7) - 8px)';
        // Extract time in HH:MM format
        const startTimeStr = `${startTime.getHours().toString().padStart(2, '0')}:${startTime.getMinutes().toString().padStart(2, '0')}`;
        const endTimeStr = `${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}`;
        // UPDATED: Use index-based color instead of instrument color
        const bgColor = getLessonChipColor(lessonIndexInDay);
        // Check if this is a temporary lesson (optimistic update)
        const isTemporary = lesson.id.startsWith('temp_');
        lessonElements.push(<div key={lesson.id} className="absolute z-10 pointer-events-auto" style={{
          top: `${top}px`,
          height: `${height}px`,
          left,
          width
        }} data-id="element-705">
            <LessonChip id={lesson.id} title={lesson.title || 'Lesson'} startTime={startTimeStr} endTime={endTimeStr} studentName={lesson.studentName || 'Student'} instrument={lesson.instrument} color={bgColor} status={lesson.status} variant="compact" onClick={(id, e) => onLessonClick(lesson, e)} isTemporary={isTemporary} className="h-full w-full" data-id="element-706" />
          </div>);
      } catch (err) {
        console.error('Error rendering lesson:', err);
      }
    });
    return lessonElements;
  };
  // Format week range for display
  const formatWeekRange = (): string => {
    const firstDay = weekDays[0];
    const lastDay = weekDays[weekDays.length - 1];
    const firstMonth = firstDay.toLocaleDateString('en-US', {
      month: 'short'
    });
    const lastMonth = lastDay.toLocaleDateString('en-US', {
      month: 'short'
    });
    const firstDate = firstDay.getDate();
    const lastDate = lastDay.getDate();
    const year = lastDay.getFullYear();
    if (firstMonth === lastMonth) {
      return `${firstMonth} ${firstDate}-${lastDate}, ${year}`;
    }
    return `${firstMonth} ${firstDate} - ${lastMonth} ${lastDate}, ${year}`;
  };
  return <div className="flex flex-col h-full" data-id="element-707">
      {/* Calendar Header */}
      <div className="mb-4" data-id="element-708">
        {/* Desktop layout - unchanged */}
        <div className="hidden md:flex justify-between items-center" data-id="element-709">
          <div className="flex items-center space-x-3" data-id="element-710">
            <button onClick={onTodayClick} className={`px-4 py-2 rounded-md text-sm border-2 transition-colors ${isDark ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} data-id="element-711">
              Today
            </button>
            <div className="flex items-center" data-id="element-712">
              <button onClick={onPrevWeek} className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`} aria-label="Previous week" data-id="element-713">
                <ChevronLeftIcon className="h-5 w-5" data-id="element-714" />
              </button>
              <span className={`text-lg font-medium mx-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-715">
                {formatWeekRange()}
              </span>
              <button onClick={onNextWeek} className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`} aria-label="Next week" data-id="element-716">
                <ChevronRightIcon className="h-5 w-5" data-id="element-717" />
              </button>
            </div>
          </div>
          <div className="flex gap-2" data-id="element-718">
            <CalendarViewSelector currentView="week" onViewChange={onViewChange} isDark={isDark} availableViews={['day', 'week', 'month']} data-id="element-719" />
          </div>
        </div>

        {/* Mobile layout - restructured */}
        <div className="md:hidden flex flex-col space-y-3" data-id="element-720">
          {/* First row: Navigation arrows + Date range (single line, centered) */}
          <div className="flex items-center justify-center" data-id="element-721">
            <button onClick={onPrevWeek} className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`} aria-label="Previous week" data-id="element-722">
              <ChevronLeftIcon className="h-5 w-5" data-id="element-723" />
            </button>
            <span className={`text-base font-medium mx-3 whitespace-nowrap ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-724">
              {formatWeekRange()}
            </span>
            <button onClick={onNextWeek} className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`} aria-label="Next week" data-id="element-725">
              <ChevronRightIcon className="h-5 w-5" data-id="element-726" />
            </button>
          </div>

          {/* Second row: Month/Week toggle (centered, under date) */}
          <div className="flex gap-2 justify-center" data-id="element-727">
            <CalendarViewSelector currentView="week" onViewChange={onViewChange} isDark={isDark} availableViews={['day', 'week', 'month']} data-id="element-728" />
          </div>
        </div>
      </div>
      {/* Week View Grid */}
      <div className={`flex-1 flex flex-col rounded-lg border ${isDark ? 'border-navy-900/70' : 'border-gray-200'} overflow-hidden`} data-id="element-729">
        {/* Day Headers */}
        <div className={`grid grid-cols-8 ${isDark ? 'bg-navy-900' : 'bg-gray-100'} rounded-t-lg sticky top-0 z-20`} data-id="element-730">
          {/* Empty cell for time gutter */}
          <div className={`p-3 ${isDark ? 'bg-navy-900' : 'bg-gray-100'}`} data-id="element-731"></div>
          {/* Day headers */}
          {weekDays.map((day, index) => {
          const isCurrentDay = isToday(day);
          return <div key={index} className={`p-3 text-center ${isDark ? 'bg-navy-800' : 'bg-white'} 
                  ${index < 6 ? isDark ? 'border-r border-navy-900/70' : 'border-r border-gray-200/70' : ''}`} data-id="element-732">
                <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-733">
                  {day.toLocaleDateString('en-US', {
                weekday: 'short'
              })}
                </div>
                <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-734">
                  {day.getDate()}
                </div>
              </div>;
        })}
        </div>
        {/* Scrollable Time Grid */}
        <div ref={scrollContainerRef} className={`flex-1 overflow-y-auto dark ${isDark ? 'bg-navy-800' : ''}`} style={{
        overscrollBehavior: 'contain'
      }} data-id="element-735">
          <div ref={timeGridRef} className="relative grid grid-cols-8" style={{
          gridTemplateRows: `repeat(${timeSlots.length}, ${hourHeight}px)`
        }} data-id="element-736">
            {/* Time labels column */}
            <div className="col-span-1" data-id="element-737">
              {timeSlots.map(hour => <div key={hour} className={`h-[60px] flex items-center justify-end pr-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-738">
                  {getFormattedTime(hour)}
                </div>)}
            </div>
            {/* Day columns */}
            {weekDays.map((day, dayIndex) => <div key={dayIndex} className="col-span-1 relative" style={{
            gridColumn: dayIndex + 2
          }} data-id="element-739">
                {/* Hour cells */}
                {timeSlots.map(hour => <div key={`${dayIndex}-${hour}`} className={`h-[60px] ${isDark ? 'border-t border-navy-900/70' : 'border-t border-gray-200/70'} 
                      ${dayIndex < 6 ? isDark ? 'border-r border-navy-900/70' : 'border-r border-gray-200/70' : ''}
                      ${isDark ? 'hover:bg-navy-700/30' : 'hover:bg-gray-50'} calendar-time-slot`} onClick={() => {
              // CRITICAL: Check popover state FIRST (same guard as Month view)
              // If lesson popover is open, close it and don't trigger time slot click
              if (popoverEvent && onClosePopover) {
                onClosePopover();
                return;
              }
              // Popover is closed, proceed with normal time slot click
              onTimeSlotClick(day, hour, 0);
            }} data-id="element-740"></div>)}
              </div>)}
            {/* FIXED: Render lessons with proper container bounds */}
            <div className="absolute top-0 bottom-0 pointer-events-none" style={{
            left: 'calc(100% / 8)',
            right: '0',
            width: 'calc(100% * 7 / 8)'
          }} data-id="element-741">
              {renderLessons()}
            </div>
          </div>
        </div>
      </div>
      {/* Legend - lesson status */}
      <div className="mt-4 flex flex-wrap gap-4" data-id="element-742">
        <div className="flex items-center" data-id="element-743">
          <div className="w-3 h-3 rounded-full bg-[#10B981] mr-2" data-id="element-744"></div>
          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-745">
            Confirmed
          </span>
        </div>
        <div className="flex items-center" data-id="element-746">
          <div className="w-3 h-3 rounded-full bg-[#3B82F6] mr-2" data-id="element-747"></div>
          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-748">
            Completed
          </span>
        </div>
        <div className="flex items-center" data-id="element-749">
          <div className="w-3 h-3 rounded-full bg-[#EAB308] mr-2" data-id="element-750"></div>
          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-751">
            Pending
          </span>
        </div>
        <div className="flex items-center" data-id="element-752">
          <div className="w-3 h-3 rounded-full bg-[#EF4444] mr-2" data-id="element-753"></div>
          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-754">
            Canceled
          </span>
        </div>
      </div>
    </div>;
};